import * as React from "react";

import { useGetNoContractReportQuery } from "@/api/spend";
import { downloadCsv, toCsv } from "@/helpers/csv";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import type { NoContractRow, SpendReconciliationBucket } from "@/types/spend";
import { NewBadge } from "./components/new-badge";
import { ReportLayout } from "./components/report-layout";
import { ReportStatus } from "./components/report-status";
import type { ReportColumn } from "./components/report-table";
import {
  REPORT_REFETCH_SECONDS,
  amount,
  creditNotes,
  gbp,
  matchesSearch,
  monthParam,
  supplierLabel,
} from "./report-utils";
import type { ReportViewProps } from "./types";

const BLANK_ID = "(blank)";

const lines = (bucket: SpendReconciliationBucket) =>
  `${gbp(bucket.spend_gbp)} (${bucket.transaction_count} lines${
    bucket.missing_rate_count ? ` + ${bucket.missing_rate_count} without a rate` : ""
  })`;

export const NoContractReportView = ({ state, onStateChange }: ReportViewProps) => {
  const { formatMonthYear } = useDateFormatter();
  const { data, isLoading, isFetching, isError, refetch } = useGetNoContractReportQuery(
    { month: state.month ? monthParam(state.month) : undefined },
    { refetchOnMountOrArgChange: REPORT_REFETCH_SECONDS },
  );
  const rows = React.useMemo(
    () =>
      (data?.rows ?? []).filter(row =>
        matchesSearch(state.search, [row.contract_id ?? BLANK_ID, ...row.supplier_names]),
      ),
    [data?.rows, state.search],
  );

  if (isLoading || isError || !data) return <ReportStatus isLoading={isLoading} onRetry={refetch} />;

  const { summary, previous_summary: previous, reconciliation } = data;
  const previousLabel = formatMonthYear(previous.month);
  const columns: ReportColumn<NoContractRow>[] = [
    {
      header: "Contract ID",
      cell: row => (
        <span className="font-semibold">
          {row.contract_id ?? BLANK_ID}
          {row.is_new ? <NewBadge /> : null}
        </span>
      ),
    },
    { header: "Supplier", cell: supplierLabel },
    { header: "Txns", numeric: true, cell: row => row.transaction_count },
    {
      header: "Spend this month",
      numeric: true,
      cell: row => <strong>{gbp(row.spend_gbp)}</strong>,
      total: gbp(summary.spend_gbp),
    },
    {
      header: "Credit notes",
      numeric: true,
      cell: row => creditNotes(row.credits_gbp, row.credit_count),
      total: creditNotes(summary.credits_gbp, summary.credit_count),
    },
    { header: "Cumulative to date", numeric: true, cell: row => gbp(row.cumulative_gbp) },
    { header: "First month seen", cell: row => formatMonthYear(row.first_month) },
  ];
  const exportCsv = (shown: NoContractRow[]) =>
    downloadCsv(
      toCsv([
        [
          "Month",
          "Contract ID",
          "Supplier name(s)",
          "Transactions",
          "Spend GBP",
          "Credit notes",
          "Credit notes GBP",
          "Cumulative to date GBP",
          "First month seen",
          "New this month",
        ],
        ...shown.map(row => [
          monthParam(data.month),
          row.contract_id ?? BLANK_ID,
          supplierLabel(row),
          row.transaction_count,
          amount(row.spend_gbp),
          row.credit_count,
          amount(row.credits_gbp),
          amount(row.cumulative_gbp),
          monthParam(row.first_month),
          row.is_new ? "Yes" : "No",
        ]),
      ]),
      `spend-with-no-contract-${monthParam(data.month)}.csv`,
    );

  return (
    <ReportLayout
      state={state}
      onStateChange={onStateChange}
      data={data}
      isFetching={isFetching}
      rows={rows}
      searchLabel="Search contract ID or supplier"
      notes={
        <>
          <p>
            Excluded because a contract exists in the application: {reconciliation.matched.id_count} IDs,{" "}
            {gbp(reconciliation.matched.spend_gbp)} this month. Transactions with a blank ID are shown on their own row.
          </p>
          <p>
            All lines this month: {lines(reconciliation.total)} = with a contract {lines(reconciliation.matched)} + no
            contract {lines(reconciliation.unmatched)} + blank ID {lines(reconciliation.blank)}.
          </p>
        </>
      }
      cards={[
        {
          title: "Spend with no contract",
          value: gbp(summary.spend_gbp),
          detail: `${previousLabel}: ${gbp(previous.spend_gbp)}`,
        },
        {
          title: "IDs with spend",
          value: String(summary.id_count),
          detail: `${summary.has_blank_id ? "Plus blank-ID lines · " : ""}${previousLabel}: ${previous.id_count}`,
        },
        {
          title: "Transactions",
          value: String(summary.transaction_count),
          detail: `${previousLabel}: ${previous.transaction_count}`,
        },
        {
          title: "New IDs this month",
          value: String(summary.new_id_count),
          detail: `${gbp(summary.new_spend_gbp)} this month · ${previousLabel}: ${previous.new_id_count}`,
        },
      ]}
      chart={{
        title: "Spend with no contract by month",
        subtitle: "IDs with no contract in the application, GBP",
        idLabel: "IDs",
      }}
      table={{
        label: "Spend with no contract",
        columns,
        rowKey: row => row.contract_id ?? "",
        totalLabel: `Month total, ${summary.id_count} IDs${summary.has_blank_id ? " plus blank-ID transactions" : ""}`,
        emptyTitle: "No spend with no contract",
      }}
      onExport={exportCsv}
    />
  );
};
